import { Fragment, type ReactNode } from 'react';
import { ChatChart, parseChartSpec } from './ChatCharts';
import { ChatFileCard, parseFileSpec } from './ChatFileCard';
import { ChatLinkCard, parseLinkSpec } from './ChatLinkCard';
import { SmartLink } from '../ui/SmartLink';
import { isTableStart, parseTable, type TableAlign } from '../../lib/chatTables';

/**
 * Minimal, dependency-free Markdown renderer for assistant messages (CLE-31).
 *
 * Supports the subset Claude tends to emit in chat: headings, bold/italic,
 * inline code, fenced code blocks, unordered/ordered lists, links and
 * paragraphs. It deliberately avoids a full Markdown engine to keep the
 * frontend bundle lean and avoid pulling in a new dependency.
 *
 * Inline charts (CLE-276): a fenced ```firmio-chart block carrying a JSON spec
 * renders as a CSS/SVG chart (KPI cards, bars, donut, table, timeline, …) via
 * components/chat/ChatCharts. This lets simulated Test Mode conversations show
 * rich, professional visuals while keeping turns as plain markdown strings.
 *
 * Safety: we never use dangerouslySetInnerHTML. Text is rendered as React
 * children, so user/assistant content cannot inject HTML.
 */

interface MarkdownProps {
  content: string;
}

// --- Inline formatting -----------------------------------------------------
// Tokenise a single line into React nodes, handling **bold**, *italic*,
// `code` and [text](url) links. Processed in a single pass with a regex.
const INLINE_RE = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const parts = text.split(INLINE_RE);
  return parts.map((part, i) => {
    const key = `${keyPrefix}-${i}`;
    // Bold/italic wrap other inline markup — the model likes to write
    // **[Aprobă factura](url)** — so their content is parsed again.
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={key} className="font-semibold">
          {renderInline(part.slice(2, -2), key)}
        </strong>
      );
    }
    if (part.startsWith('*') && part.endsWith('*')) {
      return <em key={key}>{renderInline(part.slice(1, -1), key)}</em>;
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code key={key} className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em]">
          {part.slice(1, -1)}
        </code>
      );
    }
    const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
    if (link) {
      return (
        <SmartLink
          key={key}
          href={link[2]}
          className="break-all text-primary underline underline-offset-2 hover:opacity-80"
        >
          {link[1]}
        </SmartLink>
      );
    }
    return <Fragment key={key}>{part}</Fragment>;
  });
}

// --- Block parsing ---------------------------------------------------------

type Block =
  | { kind: 'heading'; level: number; text: string }
  | { kind: 'code'; text: string }
  | { kind: 'chart'; text: string }
  | { kind: 'file'; text: string }
  | { kind: 'link'; text: string }
  | { kind: 'table'; headers: string[]; align: TableAlign[]; rows: string[][] }
  | { kind: 'ul'; items: string[] }
  | { kind: 'ol'; items: string[] }
  | { kind: 'p'; text: string };

function parseBlocks(content: string): Block[] {
  const lines = content.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block — capture the info string after the opening fence so a
    // ```firmio-chart block becomes an inline chart instead of raw code.
    if (line.trimStart().startsWith('```')) {
      const lang = line.trimStart().slice(3).trim().toLowerCase();
      const codeLines: string[] = [];
      i += 1;
      while (i < lines.length && !lines[i].trimStart().startsWith('```')) {
        codeLines.push(lines[i]);
        i += 1;
      }
      i += 1; // skip closing fence
      const text = codeLines.join('\n');
      // ```firmio-file carries the JSON card of a stored file (a contract, the
      // firm's registration certificate…) with its download link.
      blocks.push(
        lang === 'firmio-chart'
          ? { kind: 'chart', text }
          : lang === 'firmio-file'
            ? { kind: 'file', text }
            : lang === 'firmio-link'
              ? { kind: 'link', text }
              : { kind: 'code', text },
      );
      continue;
    }

    // Heading
    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      blocks.push({ kind: 'heading', level: heading[1].length, text: heading[2] });
      i += 1;
      continue;
    }

    // Pipe table (header + separator + rows).
    if (isTableStart(lines, i)) {
      const table = parseTable(lines, i);
      blocks.push({ kind: 'table', headers: table.headers, align: table.align, rows: table.rows });
      i = table.end;
      continue;
    }

    // Unordered list
    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
        i += 1;
      }
      blocks.push({ kind: 'ul', items });
      continue;
    }

    // Ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ''));
        i += 1;
      }
      blocks.push({ kind: 'ol', items });
      continue;
    }

    // Blank line
    if (line.trim() === '') {
      i += 1;
      continue;
    }

    // Paragraph: everything up to the next special block, blank lines included.
    //
    // Markdown's own rule — a single newline is just a space, blank lines
    // collapse — is right for prose but wrong for a chat. People lay a message
    // out with Shift+Enter and expect to get back exactly what they typed;
    // joining the lines with a space put them all on one row and swallowed the
    // empty lines between them. The text is kept verbatim and rendered with
    // `whitespace-pre-wrap`, so every break the writer made survives.
    const paraLines: string[] = [];
    while (
      i < lines.length &&
      !lines[i].trimStart().startsWith('```') &&
      !/^(#{1,4})\s+/.test(lines[i]) &&
      !/^\s*[-*]\s+/.test(lines[i]) &&
      !/^\s*\d+\.\s+/.test(lines[i]) &&
      !isTableStart(lines, i)
    ) {
      paraLines.push(lines[i]);
      i += 1;
    }
    // Blank lines at the very edges of the block would show as a stray gap
    // against the block spacing; the ones between the words are the point.
    while (paraLines.length > 0 && paraLines[0].trim() === '') paraLines.shift();
    while (paraLines.length > 0 && paraLines[paraLines.length - 1].trim() === '') paraLines.pop();
    if (paraLines.length === 0) continue;
    blocks.push({ kind: 'p', text: paraLines.join('\n') });
  }

  return blocks;
}

export function Markdown({ content }: MarkdownProps) {
  const blocks = parseBlocks(content);

  return (
    <div className="space-y-2 break-words text-sm leading-relaxed">
      {blocks.map((block, idx) => {
        const key = `b-${idx}`;
        switch (block.kind) {
          case 'heading': {
            const sizes = ['text-lg', 'text-base', 'text-sm', 'text-sm'];
            return (
              <p key={key} className={`font-semibold ${sizes[block.level - 1] ?? 'text-sm'}`}>
                {renderInline(block.text, key)}
              </p>
            );
          }
          case 'code':
            return (
              <pre
                key={key}
                className="overflow-x-auto rounded-md bg-muted p-3 font-mono text-xs text-foreground"
              >
                <code>{block.text}</code>
              </pre>
            );
          case 'chart': {
            const spec = parseChartSpec(block.text);
            return spec ? <ChatChart key={key} spec={spec} /> : null;
          }
          case 'file': {
            const spec = parseFileSpec(block.text);
            return spec ? <ChatFileCard key={key} spec={spec} /> : null;
          }
          case 'link': {
            const spec = parseLinkSpec(block.text);
            return spec ? <ChatLinkCard key={key} spec={spec} /> : null;
          }
          case 'table': {
            const alignClass = (a: TableAlign) =>
              a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left';
            return (
              <div key={key} className="my-1 overflow-x-auto rounded-lg border border-border">
                <table className="w-full border-collapse text-xs">
                  <thead>
                    <tr className="bg-muted/40">
                      {block.headers.map((h, c) => (
                        <th
                          key={c}
                          className={`whitespace-nowrap border-b border-border px-3 py-2 font-semibold text-muted-foreground ${alignClass(block.align[c])}`}
                        >
                          {renderInline(h, `${key}-h${c}`)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row, r) => (
                      <tr key={r} className="odd:bg-transparent even:bg-muted/20">
                        {row.map((cell, c) => (
                          <td
                            key={c}
                            className={`border-b border-border/60 px-3 py-1.5 align-top text-foreground ${alignClass(block.align[c])} ${
                              block.align[c] === 'right' ? 'whitespace-nowrap tabular-nums' : ''
                            }`}
                          >
                            {renderInline(cell, `${key}-r${r}c${c}`)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }
          case 'ul':
            return (
              <ul key={key} className="list-disc space-y-1 pl-5">
                {block.items.map((it, j) => (
                  <li key={`${key}-${j}`}>{renderInline(it, `${key}-${j}`)}</li>
                ))}
              </ul>
            );
          case 'ol':
            return (
              <ol key={key} className="list-decimal space-y-1 pl-5">
                {block.items.map((it, j) => (
                  <li key={`${key}-${j}`}>{renderInline(it, `${key}-${j}`)}</li>
                ))}
              </ol>
            );
          default:
            // `whitespace-pre-wrap`: keeps the writer's line breaks and blank
            // lines, still wraps long lines at the bubble's edge.
            return (
              <p key={key} className="whitespace-pre-wrap">
                {renderInline(block.text, key)}
              </p>
            );
        }
      })}
    </div>
  );
}
